import React, { useEffect, useState, useRef } from "react";
import { observer } from "mobx-react";
import { isEqual } from "lodash";
import { useParams } from "react-router-dom";
import cn from "classnames";

// Import the NEW TypeScript editor from your new project (as a package/library)
import { useEditor as useEditorV2 } from "../../../../libs/editor/index.js"; // Your new TypeScript library

// Import your existing constants
import { ReportDocumentSection } from "@app/constants";

// Import your existing stores
import dictionaryStore from "@app/state/store/report/dictionary/pick";
import abbreviationStore from "@app/state/store/report/abbreviation/pick";
import citationsStore from "@app/state/store/report/citation/pick";
import imageStore from "@app/state/store/report-document/image";
import tableStore from "@app/state/store/report-document/table";
import commentStore from "@app/state/store/report-document/comment";
import crossReferenceStore from "@app/state/store/report-document/cross-reference";
import captisLinktStore from "@app/state/store/report-document/captis-link";

// Import ALL your existing custom modules to pass to the new editor
import { CaptionListItem } from "./modules/appendix";
import { DictionaryPlugin } from "./modules/dictionary";
import { LoadingPlugin } from "./modules/loading";
import { ZoomPlugin } from "./modules/zoom";
import { ClipboardPlugin } from "./modules/clipboard";
import { LandscapePlugin } from "./modules/landscape";
import CustomCommands from "./modules/utils/commands";
import PastePlugin from "./modules/paste";
import OrderedList from "./modules/list/ordered";
import { Table, TableDependencies } from "./modules/table";
import { InsertMark, DeleteMark, VersionExtension } from "./modules/version";
import { Image } from "./modules/image";
import { Abbreviation } from "./modules/abbreviation";
import { CommentMark } from "./modules/comment";
import { PageBreak } from "./modules/page-break";
import { Citation } from "./modules/citation";
import { CitationGroup } from "./modules/citation-group";
import { CrossReference } from "./modules/cross-reference";
import { CaptisLinkMark } from "./modules/captis-link";
import { Link } from "./modules/link";
import { NodePosition } from "./modules/node-position";
import { HelpTextMark } from "./modules/help-text";
import { SampleTextMark } from "./modules/sample-text";
import { SymbolPicker } from "./modules/symbol-picker";
import { Checkbox } from "./modules/checkbox";
import { CustomObject } from "./modules/custom-object";
import { HighlightMark } from "./modules/highlight";
import { processCitations } from "./modules/citation-group/index";
import { PreserveMergeOnPaste } from "./modules/table/preserve-merge-on-paste";
import { ReportView } from "@app/components/report-document/editor/modules/report/index";
import Paragraph from "./modules/paragraph/paragraph";
import ListItem from "./modules/list/list-item";
import BulletList from "./modules/list/bullet-list";
import { verticalAlign } from "./modules/vertical-align";
import { validElements } from "./utils";

// Import your existing plugin components
import Content from "./content";
import EventPublisher from "./event-publisher";
import { ImagePlugin } from "./modules/image";
import { TablePlugin } from "./modules/table";
import { DictionaryPicker } from "./modules/dictionary";
import { AbbreviationPlugin } from "./modules/abbreviation";
import { LoadingMask } from "./modules/loading";
import { ClipboardWarning } from "./modules/clipboard";
import { CitationPlugin } from "./modules/citation";
import { LinkPlugin } from "./modules/link";
import EditorModal from "./modules/editor-modal";
// import { CommentPlugin } from "./modules/comment";
import { CustomObjectPlugin } from "./modules/custom-object";
import HelpTextDeleteWarning from "./modules/help-text/help-text-delete-notification";
import { useCursorPositionTracking } from "@app/hooks/useCursorPositionTracking";

import "./styles/editor.scoped.scss";

const Editor = observer(
    ({
        value,
        onChange,
        onEditorReady,
        dictionary,
        abbreviation,
        metadata = {},
        readonly = false,
        project,
        templateMode = false,
        historyMode = false,
        contentClassName,
        isPreviewMode = false,
    }) => {
        const { project: projectId, documentId } = useParams();
        const { sectionId, clientId } = metadata;
        const [isReady, setReady] = useState(false);
        const hasInitialFocusRef = useRef(false);
        const readonlyRef = useRef(readonly);
        readonlyRef.current = readonly;

        /**
         * Define the debounced function to handle update events
         */
        const onUpdate = (editor) => {
            if (onChange) {
                onChange(editor);
            }
        };

        // Prepare stores for the new editor
        const stores = {
            dictionaryStore,
            abbreviationStore,
            citationsStore,
            imageStore,
            tableStore,
            commentStore,
            crossReferenceStore,
            captisLinkStore: captisLinktStore,
        };

        // Prepare modules for the new editor
        const modules = {
            CaptionListItem,
            DictionaryPlugin,
            LoadingPlugin,
            ZoomPlugin,
            ClipboardPlugin,
            LandscapePlugin,
            CustomCommands,
            PastePlugin,
            OrderedList,
            Table,
            // the vendored editor build is regenerated by the captis-editor
            // pipeline, so it cannot be hand edited: TableDependencies is its
            // only slot spread into the extension list
            TableDependencies: [...TableDependencies, Checkbox],
            PreserveMergeOnPaste,
            InsertMark,
            DeleteMark,
            VersionExtension,
            Image,
            Abbreviation,
            CommentMark,
            PageBreak,
            Citation,
            CitationGroup,
            CrossReference,
            CaptisLinkMark,
            Link,
            NodePosition,
            HelpTextMark,
            SampleTextMark,
            SymbolPicker,
            CustomObject,
            HighlightMark,
            ReportView,
            Paragraph,
            ListItem,
            BulletList,
            verticalAlign,
            processCitations,
            validElements,
            ReportDocumentSection,
        };

        /**
         * Configure the editor to V2
         */
        const editor = useEditorV2({
            content: value,
            onUpdate: ({ editor }) => {
                onUpdate(editor);
            },
            editorProps: {
                clientId,
                projectId,
                sectionId,
                documentId,
                templateMode,
            },
            metadata,
            readonly,
            templateMode,
            stores,
            modules,
        });

        const focusEditor = () => {
            if (editor && !readonly) {
                // trying to address a race condition in which the editor is
                // destroyed before we focus it
                setTimeout(() => {
                    !editor.isDestroyed &&
                        editor.commands.focus("start", { scrollIntoView: false });
                }, 10);

                const { selection } = editor.state;
                if (selection?.node?.type?.name === "image") {
                    editor.commands.createParagraphNear();
                }
            }
        };

        // Listen to content changes and update the editor state
        useEffect(() => {
            if (isReady && editor && value?.type && !isEqual(editor.getJSON(), value)) {
                editor.commands.setContent(value);
            }
        }, [value, editor]);

        // Focus the editor when it becomes editable.
        useEffect(() => {
            if (!editor) {
                return;
            }
            if (!readonly && editor.isEditable) {
                if (!hasInitialFocusRef.current) {
                    focusEditor();
                    hasInitialFocusRef.current = true;
                }
            } else if (readonly) {
                hasInitialFocusRef.current = false;
            }
        }, [editor, readonly]);

        // Track cursor position and scroll state
        useCursorPositionTracking(editor, projectId, sectionId, readonly);

        // Re-scroll to cursor when report nodes finish loading
        useEffect(() => {
            if (!editor || readonly) {
                return;
            }

            const scrollToCursor = () => {
                if (readonlyRef.current || editor.isDestroyed) {
                    return;
                }

                try {
                    const selection = editor.view.state.selection;
                    const { $from } = selection;

                    if ($from.pos <= 1) {
                        return;
                    }

                    const domAtPos = editor.view.domAtPos($from.pos);

                    if (domAtPos && domAtPos.node) {
                        const node =
                            domAtPos.node.nodeType === 3
                                ? domAtPos.node.parentElement
                                : domAtPos.node;

                        if (node && typeof node.scrollIntoView === "function") {
                            const rect = node.getBoundingClientRect();
                            const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

                            if (!isVisible) {
                                node.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                    inline: "nearest",
                                });
                            }
                        }
                    }
                } catch (e) {
                    // Fallback: use ProseMirror's scrollIntoView
                    try {
                        editor.view.dispatch(editor.view.state.tr.scrollIntoView());
                    } catch (fallbackError) {
                        console.error("Failed to scroll to cursor:", fallbackError);
                    }
                }
            };

            editor.on("reportLoaded", scrollToCursor);

            return () => {
                editor.off("reportLoaded", scrollToCursor);
            };
        }, [editor, readonly]);

        useEffect(() => {
            if (editor) {
                editor.sectionId = metadata.sectionId;
                setReady(true);
                onEditorReady(editor);
            }
        }, [editor, onEditorReady]);

        if (!editor) {
            return null;
        }

        const hideHelpText = () => {
            if (templateMode) {
                return false;
            }

            if (readonly) {
                return true;
            }

            if (editor.storage && ReportDocumentSection.HELP_TEXT?.MARK_NAME) {
                return !editor.storage[ReportDocumentSection.HELP_TEXT.MARK_NAME]?.store?.isVisible;
            }

            // Fallback for V1 editor
            return !editor.storage[ReportDocumentSection.HELP_TEXT.MARK_NAME]?.store?.isVisible;
        };

        return (
            <div
                className={cn({
                    editor: true,
                    hideHelpText: hideHelpText(),
                })}
            >
                <Content
                    editor={editor}
                    sectionId={metadata.sectionId}
                    project={project}
                    templateMode={templateMode}
                    historyMode={historyMode}
                    contentClassName={contentClassName}
                    readonly={readonly}
                    isPreviewMode={isPreviewMode}
                />
                <DictionaryPicker
                    editor={editor}
                    dictionary={dictionary}
                    templateMode={templateMode}
                    documentId={documentId}
                />
                <TablePlugin
                    editor={editor}
                    key={editor.sectionId ?? `table-${editor.sectionId}`}
                />
                <LoadingMask editor={editor} />
                <ImagePlugin editor={editor} />
                <AbbreviationPlugin
                    editor={editor}
                    abbreviation={abbreviation}
                    templateMode={templateMode}
                    documentId={documentId}
                />
                <ClipboardWarning editor={editor} />
                {!templateMode && <CitationPlugin editor={editor} />}
                <LinkPlugin editor={editor} />
                <EditorModal editor={editor} />
                <EventPublisher />
                {/**!historyMode && (
                    <CommentPlugin editor={editor} sectionId={sectionId} readonly={readonly} />
                )*/}
                <CustomObjectPlugin editor={editor} sectionId={sectionId} readonly={readonly} />
                <HelpTextDeleteWarning editor={editor} />
            </div>
        );
    },
);

Editor.displayName = "Editor";

export default Editor;
